import i18next from 'i18next';
import moment from 'moment-timezone';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { getFactorToConvertMetricToEntityUnit } from '@commons/utils/conversion';
import utilsXLS from '@commons/utils/makeXLS';

import { TRANSFER_STATUS_LABELS, TRANSFER_STATUS } from '../../../utils/constants';

const getSupplierProductsSheetColumns = (storeName) => {
  const columns = [
    {
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_REFERENCE'),
      propertyKey: 'reference',
    },
    {
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_SENDER', { storeName }),
      propertyKey: 'senderStoreName',
    },
    {
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_RECIPIENT', { storeName }),
      propertyKey: 'recipientStoreName',
    },
    {
      name: i18next.t('GENERAL.DATE'),
      propertyKey: 'sentAt',
      transform: (date) => moment(date).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
    },
    {
      name: i18next.t('GENERAL.STATUS'),
      propertyKey: 'status',
      transform: (status) => i18next.t(TRANSFER_STATUS_LABELS[status]),
    },
    {
      name: i18next.t('GENERAL.SKU'),
      propertyKey: 'sku',
    },
    {
      name: i18next.t('GENERAL.SUPPLIER_PRODUCT'),
      propertyKey: 'name',
    },
    {
      name: i18next.t('GENERAL.PACKAGING'),
      propertyKey: 'packaging',
    },
    {
      name: i18next.t('STOCKS.TRANSFERS.EXPORT_PRICE_PER_PACKAGING'),
      propertyKey: 'pricePerPackaging',
    },
    {
      name: i18next.t('STOCKS.TRANSFERS.EXPORT_TRANSFER_NOTE_QUANTITY'),
      propertyKey: 'transferNoteQuantity',
    },
    {
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_BDL'),
      propertyKey: 'deliveryNoteQuantity',
    },
    {
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_RECEIVED'),
      propertyKey: 'receivedQuantity',
    },
    {
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_TOTAL_BDL'),
      propertyKey: 'totalDelivered',
    },
    {
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_TOTAL_RECEIVED'),
      propertyKey: 'totalReceived',
    },
    {
      name: i18next.t('ORDERS.ORDERS.EXPORT_VAT_RATE'),
      propertyKey: 'vatRate',
    },
    {
      name: i18next.t('GENERAL.CURRENCY'),
      propertyKey: 'currency',
    },
  ];

  return columns;
};

const getRecipesSheetColumns = (storeName) => {
  const columns = [
    {
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_REFERENCE'),
      propertyKey: 'reference',
    },
    {
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_SENDER', { storeName }),
      propertyKey: 'senderStoreName',
    },
    {
      name: i18next.t('STOCKS.TRANSFERS.CONTENT_EXPORT_RECIPIENT', { storeName }),
      propertyKey: 'recipientStoreName',
    },
    {
      name: i18next.t('GENERAL.DATE'),
      propertyKey: 'sentAt',
      transform: (date) => moment(date).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR),
    },
    {
      name: i18next.t('GENERAL.STATUS'),
      propertyKey: 'status',
      transform: (status) => i18next.t(TRANSFER_STATUS_LABELS[status]),
    },
    {
      name: i18next.t('GENERAL.RECIPE'),
      propertyKey: 'name',
    },
    {
      name: i18next.t('GENERAL.UNIT'),
      propertyKey: 'unit',
    },
    {
      name: i18next.t('STOCKS.TRANSFERS.EXPORT_PRICE_EX_TAX_IN_RECIPE_UNIT'),
      propertyKey: 'price',
    },
    {
      name: i18next.t('STOCKS.TRANSFERS.EXPORT_TRANSFER_NOTE_QUANTITY'),
      propertyKey: 'transferNoteQuantity',
    },
    {
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_BDL'),
      propertyKey: 'deliveryNoteQuantity',
    },
    {
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_QUANTITY_RECEIVED'),
      propertyKey: 'receivedQuantity',
    },
    {
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_TOTAL_BDL'),
      propertyKey: 'totalDelivered',
    },
    {
      name: i18next.t('ORDERS.ORDERS.LIST_LABEL_TOTAL_RECEIVED'),
      propertyKey: 'totalReceived',
    },
    {
      name: i18next.t('GENERAL.CURRENCY'),
      propertyKey: 'currency',
    },
  ];

  return columns;
};

const formatSupplierProductsData = (commonData, transferedSupplierProducts, transferList) =>
  transferedSupplierProducts.flatMap((supplierProduct) => {
    const supplierProductData = {
      name: supplierProduct.name,
      sku: supplierProduct.sku,
      vatRate: supplierProduct.vatRate || null,
    };

    const usedPackagings = supplierProduct.packagings.filter(
      ({ transferNoteQuantity, deliveryNoteQuantity, receivedQuantity }) =>
        !!transferNoteQuantity || !!deliveryNoteQuantity || !!receivedQuantity,
    );

    return usedPackagings.map((packaging) => {
      const { deliveryNoteQuantity, receivedQuantity, selectedQuantity } = packaging;

      const formattedDeliveryNoteQuantity =
        transferList.status >= TRANSFER_STATUS.REQUESTED
          ? deliveryNoteQuantity || selectedQuantity
          : null;

      const formattedReceivedQuantity =
        transferList.status >= TRANSFER_STATUS.SENT ? receivedQuantity || selectedQuantity : null;

      return {
        ...commonData,
        ...supplierProductData,
        packaging: packaging.name,
        pricePerPackaging: packaging.price,
        transferNoteQuantity: packaging.transferNoteQuantity,
        deliveryNoteQuantity: formattedDeliveryNoteQuantity,
        receivedQuantity: formattedReceivedQuantity,
        totalDelivered:
          formattedDeliveryNoteQuantity && packaging.price
            ? formattedDeliveryNoteQuantity * packaging.price
            : null,
        totalReceived:
          formattedReceivedQuantity && packaging.price
            ? formattedReceivedQuantity * packaging.price
            : null,
      };
    });
  });

const formatRecipesData = (commonData, transferedRecipes, transferList) =>
  transferedRecipes.map((recipe) => {
    const { deliveryNoteQuantity, receivedQuantity, selectedQuantity } = recipe;

    const formattedDeliveryNoteQuantity =
      transferList.status >= TRANSFER_STATUS.REQUESTED
        ? deliveryNoteQuantity || selectedQuantity
        : null;

    const formattedReceivedQuantity =
      transferList.status >= TRANSFER_STATUS.SENT ? receivedQuantity || selectedQuantity : null;

    return {
      ...commonData,
      name: recipe.name,
      unit: recipe.unit,
      transferNoteQuantity: recipe.transferNoteQuantity,
      deliveryNoteQuantity: formattedDeliveryNoteQuantity,
      receivedQuantity: formattedReceivedQuantity,
      totalDelivered:
        formattedDeliveryNoteQuantity && recipe.price
          ? formattedDeliveryNoteQuantity *
            recipe.price *
            getFactorToConvertMetricToEntityUnit(recipe.unit)
          : null,
      totalReceived:
        formattedReceivedQuantity && recipe.price
          ? formattedReceivedQuantity *
            recipe.price *
            getFactorToConvertMetricToEntityUnit(recipe.unit)
          : null,
      price: recipe.price,
    };
  });

export const exportTransfer = ({
  transferList,
  transferedSupplierProducts,
  transferedRecipes,
  storeName,
  currency,
}) => {
  const commonData = {
    reference: transferList.reference,
    senderStoreName: transferList.senderStoreName,
    recipientStoreName: transferList.recipientStoreName,
    sentAt: transferList.sentAt,
    status: transferList.status,
    currency: currency.alphabeticCode,
  };

  const supplierProductsSheetData = formatSupplierProductsData(
    commonData,
    transferedSupplierProducts,
    transferList,
  );
  const recipesSheetData = formatRecipesData(commonData, transferedRecipes, transferList);
  const fileName = transferList.reference;

  const mainSheet = utilsXLS.generateDefaultSheet(
    i18next.t('FEATURE.ADMIN.SUPPLIERS_PRODUCTS'),
    getSupplierProductsSheetColumns(storeName),
    supplierProductsSheetData,
    currency,
  );

  const contextSheet = utilsXLS.generateDefaultSheet(
    i18next.t('FEATURE.ADMIN.RECIPES'),
    getRecipesSheetColumns(storeName),
    recipesSheetData,
    currency,
  );

  utilsXLS.makeXLS(fileName, [mainSheet, contextSheet]);
};

export default {
  exportTransfer,
};
